import { GRAVITY } from "../constants";
import type { Gear, ItemId } from "../items";
import type { Vec2 } from "../math";
import type { School } from "./schools";
import type { StreamRoute } from "./stream";

/**
 * Static world description. Fully derived from `seed`; never sent over the
 * wire, only the seed is. Player edits to asteroids are sent separately.
 */
export interface WorldDef {
  seed: number;
  worldRadius: number;
  suns: SunDef[];
  asteroids: AsteroidDef[];
  /** Eddies turning rock fields round (see CurrentDef.spin). None in a hand-made world. */
  currents?: CurrentDef[];
  /** Derelict ships the server puts out once (by id), each a short way from `position`. */
  derelicts?: DerelictDef[];
  /** Vines between the lobes of vined rocks (see VineDef). */
  vines?: VineDef[];
  /** The rock stream's track (see stream.ts). None in a hand-made world. */
  stream?: StreamRoute;
  /** Every star's ring, as the flow its rocks make going round (see flowAt). None in a hand-made world. */
  rings?: RingFlow[];
}

/** A star's ring as a flow: its rocks between `r0` and `r1` from `position`, all turning at `spin` (rad/s, anticlockwise). */
export interface RingFlow {
  position: Vec2;
  r0: number;
  r1: number;
  spin: number;
}

/**
 * A slow river of air: inside `radius` of `position` it carries things along
 * `flow`, strongest in the middle. An eddy (`spin`) turns round its middle
 * instead, like a wheel, carrying its field of rocks round with it.
 */
export interface CurrentDef {
  position: Vec2;
  radius: number;
  /** Blocks per second at the middle. */
  flow: Vec2;
  /** An eddy: how fast it turns (radians/s, anticlockwise). */
  spin?: number;
}

/** A point of interest: an old wreck adrift with loot aboard. */
export interface DerelictDef {
  id: string;
  position: Vec2;
  /** "derelict": an old hull with a chest; "hulk": a crashed pirate ship with plunder. */
  kind: "derelict" | "hulk";
  seed: number;
}

/** A fission-powered artificial sun sitting in the play plane. */
export interface SunDef {
  id: string;
  position: Vec2;
  radius: number;
  /** 0xRRGGBB */
  color: number;
  /** Light intensity multiplier. */
  output: number;
  /** "fission" (the warm ones) or "cold" (a blue sun of a different make). */
  kind?: "fission" | "cold";
  /** Its name, from the seed ("Verrin"): on the map, and in the names of the stream's corners. */
  name?: string;
}

export type AsteroidKind = "home" | "rock" | "water" | "ice" | "hollow" | "metal" | "dead" | "living" | "coal" | "iron";

/** Does a rock pull on characters (see GRAVITY)? Big rocks you live on do; small rocks, water and open space are zero-g. */
export const asteroidPulls = (def: Pick<AsteroidDef, "pulls">): boolean => !!def.pulls;

/** Should a rock pull, by its size (see GRAVITY.minRadius)? Generation sets AsteroidDef.pulls from this. */
export const bigEnoughToPull = (def: Pick<AsteroidDef, "kind" | "radius">): boolean => def.kind !== "water" && def.radius >= GRAVITY.minRadius;

/**
 * A rock you can dig into and build a home in. Its tile grid is anchored at
 * world coordinates (ox, oy): whole numbers that never change, unless the
 * rock drifts (see `drift`), when they follow it.
 */
export interface AsteroidDef {
  id: string;
  ox: number;
  oy: number;
  w: number;
  h: number;
  /** Rock radius in tiles; the grid is padded beyond this. */
  radius: number;
  seed: number;
  /**
   * "home": the huge asteroid with trees; "water": a ball of water with no
   * gravity; "rock": plain rock with caves; "ice": frozen water and rock;
   * "hollow": a shell of rock round one big cave; "metal": rich in ore;
   * "dead": burnt-out rock with an old wreck inside; "living": stone wrapped
   * in a living bark shell with branches, near a sun (see WORLD_GEN.living);
   * "coal", "iron": a nugget, a small rock almost all coal or iron ore (see
   * WORLD_GEN.minerals.nuggets).
   */
  kind: AsteroidKind;
  /** Further from the start, richer: how much more ore and coal than usual (see WORLD_GEN.minerals.wealth). Missing is 1. */
  wealth?: number;
  /** Held by pirates: turrets on its surface fire at anyone near (see PIRATE_BASE). */
  pirateBase?: boolean;
  /** It pulls on characters (see GRAVITY, asteroidPulls): you stand on it, and pieces on it turn to face out. */
  pulls?: boolean;
  /**
   * Where it is (see Environment), each 0..1: the light it gets (what grows
   * on it: patchy cover, then a full skin with plants), the heat, and how
   * wet it is. Missing is 0. Home always has full light.
   */
  light?: number;
  heat?: number;
  wet?: number;
  /** What grows on it, from its environment and kind (see biomeOf). */
  biome?: Biome;
  /** A living rock's branches reach this far past its widest outline (its grid is padded for them). */
  reach?: number;
  /** A living rock: which way its sun is (radians), so its branches grow toward it. */
  sunward?: number;
  /** One rock in 3-4 lobes with open space between them, tied together by vines (see VineDef, WORLD_GEN.vined). */
  vined?: boolean;
  /**
   * A living rock whose heart was taken: it's a husk, its foliage and
   * branches gone. Not from generation: set from the area's state, and the
   * grid made again.
   */
  dead?: boolean;
  /** Formed after the world was made (see NEW_ROCKS): kept by its area, not made from the seed. */
  spawned?: boolean;
  /**
   * A small rock of a field, which moves (see rockMotion.ts): rammed or
   * bumped, it's knocked off its path and swings back. Its path is where it
   * was made ((x, y): its grid's origin there), or in an eddy, that turned
   * round the eddy's middle, or on the stream's belt (see RockDrift.stream).
   */
  drift?: RockDrift;
  /** One of the stream's named big rocks (its name), where new players start (see STREAM.named). */
  named?: string;
  /**
   * A gorge rock (see STREAM.gorge): a static giant with a channel cut
   * through it for the stream to run through, from `a` to `b` (blocks from
   * the middle of its grid), `half` blocks either side of that line.
   */
  channel?: { a: Vec2; b: Vec2; half: number };
}

export interface RockDrift {
  x: number;
  y: number;
  /**
   * What carries it round: an eddy (see CurrentDef.spin), or a ring of a sun's
   * belts. Its middle, outer radius, the inner one of a ring (its rocks lie
   * between), and spin.
   */
  eddy?: { x: number; y: number; r: number; inner?: number; spin: number };
  /**
   * Or the stream's belt (see stream.ts): `key` the sphere it's the stream
   * of (see sphereKey), `u` where on the belt the rock's middle is (blocks
   * along the track at time 0) and `o` how far across the band (blocks to
   * the left of the way the belt goes, at the band's full width).
   */
  stream?: { key: string; u: number; o: number };
}

/** What grows on a rock (see lifeasteroids.md, "Biomes"; biomeOf). */
export type Biome = "living" | "scorched" | "meadow" | "grove" | "twilight" | "lichen" | "frost" | "dark" | "wreck" | "barren" | "water";

/**
 * A vine between two lobes of a vined rock (see AsteroidDef.vined). Made with
 * the rock from its seed (see rockVines), never sent: from `a` to `b` (blocks
 * from the middle of the rock's grid, which never turns, so the vine goes
 * wherever the rock does), hanging with `sag` (blocks) of slack. Climbed
 * along, cut through.
 */
export interface VineDef {
  id: string;
  rock: string;
  a: Vec2;
  b: Vec2;
  sag: number;
  /** Blocks across. */
  thickness: number;
  seed: number;
}

/**
 * Mutable state of a ship, replicated to clients (tiles are sent separately).
 * Everything players build is a ship: nobody owns any of it, and anyone can
 * board, fly, build on or dig any ship, from a starter ship to a single loose
 * block.
 */
/** One turret on a ship. */
export interface TurretState {
  /** Where the barrel points, in the ship's own frame: radians from its +X, between TURRET.minAim and TURRET.maxAim. */
  aim: number;
  /** Seconds until it can fire again. */
  cooldown: number;
  /** The player manning it, if anyone. */
  gunner: string | null;
}

export interface ShipState {
  id: string;
  /**
   * World position the grid's bottom-left corner would have at angle 0.
   * Ships rotate about the centre of their grid, so the centre is always at
   * `position + (w/2, h/2)`. See `shipToWorld`.
   */
  position: Vec2;
  velocity: Vec2;
  /** Radians, counter-clockwise. The nose is local +X. */
  angle: number;
  angularVelocity: number;
  /** Its turrets, by the grid index of each one's anchor (bottom-left seat tile). Made as they're first used. */
  turrets: Record<string, TurretState>;
  /** Fuel and water in the furnaces, in engine-seconds (see FURNACE). Without both, no steam. */
  fuel: number;
  water: number;
  /** Which way its engines are firing right now (-1..1 each; see ShipControl), for drawing their steam. */
  throttle: Vec2;
  /** Player currently at the helm, if any. */
  pilotId: string | null;
  /** Hits taken by blocks tough enough to need more than one (see TileDef.hits), by tile index. */
  damage?: Record<string, number>;
  /** Crewed by pirates (see PIRATES): flown and fought by the server. Cleared once it's crippled. */
  pirate?: boolean;
  /** Blocks on fire, by tile index: how long each has burned (s). See FIRE. */
  fires?: Record<string, number>;
  /** Seconds until its bow guns can fire again. */
  gunCooldown?: number;
  /** Winches switched on, by anchor index (see WINCH). */
  winches?: Record<string, boolean>;
  /** Which kind of pirate it is (see PirateKind), while it's a pirate. */
  pirateKind?: string;
  /** On a harpoon's line (see HARPOON): which harpoon (anchor index), what it's stuck in, and how long the line is. */
  tether?: Tether | null;
  /** Share of its engines' push lost (0..1) to an eel latched onto its hull (see INCOMERS.eel). */
  stalled?: number;
}

export interface Tether {
  anchor: number;
  /**
   * Where it's stuck (see tetherPoint): a world point; a point of drifting
   * rock `rock`'s grid, or of ship `ship`'s (it goes where they go); or
   * creature `creature` (whale or gas bag), where it was last (the server
   * keeps it up to date).
   */
  point: Vec2;
  rock?: string;
  ship?: string;
  creature?: string;
  length: number;
}

/** A player's character. */
export interface PlayerState {
  id: string;
  name: string;
  /**
   * Ship the character is standing inside, or null for world space. When set,
   * `position` and `velocity` are in that ship's grid coordinates (rotating
   * with it, gravity toward its floor), so the crew is carried along however
   * it flies or spins.
   */
  frame: string | null;
  /** Feet, horizontally centred. */
  position: Vec2;
  velocity: Vec2;
  grounded: boolean;
  /** Jump was held last step, so a new press can be told apart from holding it. */
  jumpHeld: boolean;
  /**
   * Double-jumped off an asteroid: coasting with no gravity or drag until they
   * touch something, board a ship, or leave its gravity.
   */
  launched: boolean;
  /** What they wear (see GearSlot): others see it, and a steampack on the back is what lets them fly (see STEAMPACK). */
  gear?: Gear;
  /** Steam left in the steampack (see STEAMPACK), 0..1; missing means full. */
  steam?: number;
  /** Blasting the steampack this step (for the puffs and the hiss). */
  jetting?: boolean;
  /**
   * Which way the character's head points, in radians (0 = up; same sense as
   * a ship's angle). Always 0 aboard a ship (its frame is already turned). On
   * an asteroid it points out from the centre, so feet face the rock wherever
   * you stand.
   */
  up: number;
  piloting: boolean;
  /** The turret they're manning: its anchor index in their ship's grid (or, see gunningAsteroid, an asteroid's), or null. */
  gunning: number | null;
  /** When the turret they're manning stands on an asteroid (they're not aboard a ship): which. */
  gunningAsteroid?: string;
  /** Where they are pointing (the mouse), in world coordinates: their head and tool follow it. */
  aim: Vec2;
  /** The item in their selected quick-access slot: the tool they swing (if it is one) and what others see in their hand. */
  holding: ItemId | null;
  /** Swinging their tool (left mouse held, on foot). */
  using: boolean;
  /** 0..HEALTH.max. At 0 they're knocked out and wake up in their bed, or on a rock in the start system. */
  health: number;
  /** Seconds since they were last hurt: health comes back after HEALTH.regenDelay. */
  sinceHurt: number;
  /** 1 = facing right, -1 = facing left. */
  facing: number;
  /** Last input sequence number the server applied (for client reconciliation). */
  lastInputSeq: number;
  /** How they look (picked on the name screen). */
  look?: Look;
  /** Where their grapple line is hooked, if it is (see GRAPPLE): a world point, or a point in ship `grappleShip`'s grid. See grapplePoint. */
  grapple?: Vec2 | null;
  grappleShip?: string | null;
  /** Hooked into a drifting rock: which (`grapple` is then a point of its grid; see hookPoint). */
  grappleRock?: string | null;
  /** Their grapple line's length: while it flies (not yet hooked) how far out it's got, toward `grappleDir` (radians); hooked, how long the line is (reeled in with W). Null when there's no line out. */
  grappleOut?: number | null;
  grappleDir?: number;
  /** Holding up to reel the grapple in, since before the line came off: up does nothing else until it's let go. */
  reelHeld?: boolean;
  /** Climbing a vine (see VINES): which, and how far along it (0..1). G lets go; so does reaching its end. */
  vine?: string | null;
  vineT?: number;
  /** Seconds before a vine may take hold again (after letting go with G). */
  vineWait?: number;
  /** Pushing any way last step (so a kick off a wall is once a push, see GRIP). */
  pushHeld?: boolean;
  /** The drifting rock that carried them last step (see carryRiders): stepping off it, they keep its speed. Not sent. */
  rode?: string | null;
}

/** A character's looks: coat colour (hue, 0..360), hat, and build. */
export interface Look {
  hue: number;
  hat: "none" | "cap" | "tricorn" | "goggles" | "bandana";
  body: "standard" | "stocky" | "slim";
  hair: "crop" | "tail" | "bun";
}

export const HATS: readonly Look["hat"][] = ["none", "cap", "tricorn", "goggles", "bandana"];
export const BODIES: readonly Look["body"][] = ["standard", "stocky", "slim"];
export const HAIRS: readonly Look["hair"][] = ["crop", "tail", "bun"];

/** A look from untrusted input: anything unknown falls back to the default. */
export function sanitizeLook(raw: unknown): Look | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Partial<Look>;
  const hue = Number(r.hue);
  return {
    hue: Number.isFinite(hue) ? ((Math.round(hue) % 360) + 360) % 360 : 30,
    hat: HATS.includes(r.hat as Look["hat"]) ? (r.hat as Look["hat"]) : "none",
    body: BODIES.includes(r.body as Look["body"]) ? (r.body as Look["body"]) : "standard",
    hair: HAIRS.includes(r.hair as Look["hair"]) ? (r.hair as Look["hair"]) : "crop",
  };
}

/** One-off things a player can do with a key or click (G: "hook"; right click holding the grapple: "grapple"), sent with the input of the step they're pressed in. */
export type InputAction = "hook" | "grapple";

/** Whales roam the open sphere (see WHALES); gas bags drift among the rocks eating trees (GASBAGS); grazers walk the grassy ones (GRAZERS); schools of skyfish circle them (SKYFISH). */
export type CreatureKind = "whale" | "gasbag" | "grazer" | "skyfish" | IncomerKind;
/** The things from outside the sphere that come in while a sun is out (see INCOMERS). */
export type IncomerKind = "spider" | "eel" | "carrier" | "warden";
export const INCOMER_KINDS: readonly IncomerKind[] = ["spider", "eel", "carrier", "warden"];
export const isIncomer = (kind: CreatureKind): kind is IncomerKind => (INCOMER_KINDS as readonly string[]).includes(kind);
export const CREATURE_KINDS: readonly CreatureKind[] = ["whale", "gasbag", "grazer", "skyfish", ...INCOMER_KINDS];

/** A creature (see CreatureKind). Moved by the server. */
export interface CreatureState {
  id: string;
  kind: CreatureKind;
  /** Where it is in the world: a whale's or gas bag's middle, a grazer's feet. */
  position: Vec2;
  velocity: Vec2;
  /** Which way it's heading, radians; a grazer's, which way is up from where it stands. */
  angle: number;
  health: number;
  /** Charging whoever hurt it last. */
  angry: boolean;
  /** By (or on) a drifting rock: where it is from that rock's grid origin, so it's drawn moving with the rock. */
  rock?: string;
  local?: Vec2;
  /** Eating: a gas bag at a tree's leaves, a grazer head down in the grass. */
  eating?: boolean;
  /** A grazer: which way it faces along the ground (1 or -1, with up as up). */
  facing?: number;
  /** Skyfish: the school (round its rock's middle; see fishOf). */
  school?: School;
  /** An incomer caught by the light coming back: frozen, its lenses dark, until it's pulled out (see NIGHT.stall). */
  stalled?: boolean;
  /** An incomer at its work: an eel latched onto a hull, a carrier lowering spiders, a warden winding up a throw. */
  busy?: boolean;
}

export interface ProjectileState {
  id: string;
  ownerShipId: string;
  position: Vec2;
  velocity: Vec2;
  /** Seconds remaining. */
  life: number;
}

/** One fixed step of player intent. Axis values are normalised -1..1. */
export interface PlayerInput {
  seq: number;
  /** Seconds this input covers. */
  dt: number;
  /** On foot: walk. At the helm: fire the bow (-1, A) or stern (+1, D) engines. */
  moveX: number;
  /** On foot: +1 = up (jump, climb). At the helm: fire the engines on the roof (-1, S) or under the keel (+1, W). */
  moveY: number;
  /** At the helm: turn the ship, +1 counter-clockwise (Q), -1 clockwise (E). */
  turn: number;
  /** Take the helm or a turret seat you stand at, or leave it (right click). True only on the step it was clicked. */
  interact: boolean;
  /** Left mouse held: at the helm, fire the cannons at the aim point; on foot, swing your tool at it. */
  fire: boolean;
  /** The item in the selected quick-access slot (the server checks you have one). */
  hold: ItemId | null;
  /** Where the mouse points, in world coordinates. The character looks and reaches toward it. */
  aimX: number;
  aimY: number;
  /** A key pressed this step: the grapple (G): at the helm, the ship's; on foot, your own. */
  action?: InputAction | null;
}

export const emptyInput = (): PlayerInput => ({
  seq: 0,
  dt: 0,
  moveX: 0,
  moveY: 0,
  turn: 0,
  interact: false,
  fire: false,
  hold: null,
  aimX: 0,
  aimY: 0,
});
